import JSZip from "jszip";

export const CORRUPTED_FILE_ERROR = (fileName: string) =>
  `The uploaded file ${fileName} is corrupted and could not be read. Please upload the resolved file version`;

export interface FileIntegrityResult {
  isValid: boolean;
  reason?: string;
}

/** Extensions that only get the zero-byte check (no known container format to validate) */
const TIER0_ONLY_EXTENSIONS = ["txt", "csv", "vnd"];

const MAX_DEEP_PARSE_BYTES = 50 * 1024 * 1024;

const bytesToAscii = (bytes: Uint8Array): string => {
  let result = "";
  for (let i = 0; i < bytes.length; i++) result += String.fromCharCode(bytes[i]);
  return result;
};

const startsWithBytes = (bytes: Uint8Array, signature: number[]): boolean =>
  bytes.length >= signature.length && signature.every((byte, i) => bytes[i] === byte);

const isZipHeader = (bytes: Uint8Array): boolean =>
  startsWithBytes(bytes, [0x50, 0x4b, 0x03, 0x04]) || startsWithBytes(bytes, [0x50, 0x4b, 0x05, 0x06]);

/** Header (magic byte) signature per supported extension. Missing entry => no header check (pass). */
const HEADER_SIGNATURES: Record<string, (bytes: Uint8Array) => boolean> = {
  pdf: (bytes) => bytesToAscii(bytes.subarray(0, 5)) === "%PDF-",
  docx: isZipHeader,
  xlsx: isZipHeader,
  zip: isZipHeader,
  doc: (bytes) => startsWithBytes(bytes, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]),
  xls: (bytes) => startsWithBytes(bytes, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]),
  jpeg: (bytes) => startsWithBytes(bytes, [0xff, 0xd8, 0xff]),
  jpg: (bytes) => startsWithBytes(bytes, [0xff, 0xd8, 0xff]),
  png: (bytes) => startsWithBytes(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
  tiff: (bytes) => startsWithBytes(bytes, [0x49, 0x49, 0x2a, 0x00]) || startsWithBytes(bytes, [0x4d, 0x4d, 0x00, 0x2a]),
  bmp: (bytes) => bytesToAscii(bytes.subarray(0, 2)) === "BM",
  rar: (bytes) => startsWithBytes(bytes, [0x52, 0x61, 0x72, 0x21, 0x1a, 0x07]),
  dwg: (bytes) => bytesToAscii(bytes.subarray(0, 4)) === "AC10",
  autocad_dwg: (bytes) => bytesToAscii(bytes.subarray(0, 4)) === "AC10",
  stp: (bytes) => bytesToAscii(bytes.subarray(0, 13)).trim().toUpperCase().startsWith("ISO-10303-21"),
};

/** Searches the last `tailWindow` bytes of the buffer for `needle`. */
const findInTail = (buffer: ArrayBuffer, needle: number[], tailWindow: number): boolean => {
  const bytes = new Uint8Array(buffer);
  const start = Math.max(0, bytes.length - tailWindow);
  outer: for (let i = start; i <= bytes.length - needle.length; i++) {
    for (let j = 0; j < needle.length; j++) {
      if (bytes[i + j] !== needle[j]) continue outer;
    }
    return true;
  }
  return false;
};

const asciiToBytes = (text: string): number[] => Array.from(text).map((c) => c.charCodeAt(0));

const validatePdfStructure = (buffer: ArrayBuffer): boolean => {
  if (!findInTail(buffer, asciiToBytes("%%EOF"), 1024)) return false;

  const bytes = new Uint8Array(buffer);
  const headText = bytesToAscii(bytes.subarray(0, Math.min(bytes.length, 1024)));
  if (headText.includes("/Linearized")) {
    const match = headText.match(/\/L\s+(\d+)/);
    if (match && parseInt(match[1], 10) !== bytes.length) return false;
  }
  return true;
};

const validateZipFamilyStructure = async (file: File, buffer: ArrayBuffer): Promise<boolean> => {
  if (!findInTail(buffer, [0x50, 0x4b, 0x05, 0x06], 65536)) return false;
  if (file.size < MAX_DEEP_PARSE_BYTES) {
    try {
      await JSZip.loadAsync(buffer);
    } catch {
      return false;
    }
  }
  return true;
};

const validateBmpStructure = (buffer: ArrayBuffer): boolean => {
  if (buffer.byteLength < 6) return false;
  const declaredSize = new DataView(buffer).getUint32(2, true);
  return declaredSize === buffer.byteLength;
};

const validateStpStructure = (buffer: ArrayBuffer): boolean => {
  const bytes = new Uint8Array(buffer);
  const tailText = bytesToAscii(bytes.subarray(Math.max(0, bytes.length - 256)));
  return /END-ISO-10303-21\s*;?/i.test(tailText);
};

const validateStructure = async (extension: string, file: File, buffer: ArrayBuffer): Promise<boolean> => {
  switch (extension) {
    case "pdf":
      return validatePdfStructure(buffer);
    case "docx":
    case "xlsx":
    case "zip":
      return validateZipFamilyStructure(file, buffer);
    case "jpeg":
    case "jpg":
      return findInTail(buffer, [0xff, 0xd9], 16);
    case "png":
      return findInTail(buffer, [0x49, 0x45, 0x4e, 0x44], 16);
    case "bmp":
      return validateBmpStructure(buffer);
    case "stp":
      return validateStpStructure(buffer);
    default:
      return true;
  }
};

export interface ICorruptedFile {
  name: string;
  reason: string;
}

/**
 * Splits files into valid vs. corrupted, reading each file's bytes exactly once.
 * Intended to be called right at file-selection time, before files are staged/uploaded.
 */
export const partitionByIntegrity = async (
  files: File[],
): Promise<{ validFiles: File[]; corruptedFiles: ICorruptedFile[] }> => {
  const validFiles: File[] = [];
  const corruptedFiles: ICorruptedFile[] = [];

  for (const file of files) {
    let corruptionReason = "";
    try {
      const arrayBuffer = await file.arrayBuffer();
      const integrityResult = await validateFileIntegrity(file, arrayBuffer);
      if (!integrityResult.isValid) {
        corruptionReason = integrityResult.reason || CORRUPTED_FILE_ERROR(file.name);
      }
    } catch {
      corruptionReason = CORRUPTED_FILE_ERROR(file.name);
    }

    if (corruptionReason) {
      corruptedFiles.push({ name: file.name, reason: corruptionReason });
    } else {
      validFiles.push(file);
    }
  }

  return { validFiles, corruptedFiles };
};

/**
 * Validates that a file's bytes are actually readable and match what its extension claims.
 * Expects the caller to have already read the file into `buffer`, so this adds no extra I/O.
 */
export const validateFileIntegrity = async (file: File, buffer: ArrayBuffer): Promise<FileIntegrityResult> => {
  if (file.size === 0) {
    return { isValid: false, reason: CORRUPTED_FILE_ERROR(file.name) };
  }

  const extension = file.name.split(".").pop()?.toLowerCase() || "";
  if (TIER0_ONLY_EXTENSIONS.includes(extension)) {
    return { isValid: true };
  }

  const headerCheck = HEADER_SIGNATURES[extension];
  if (!headerCheck) {
    return { isValid: true };
  }

  const headBytes = new Uint8Array(buffer.slice(0, Math.min(buffer.byteLength, 16)));
  if (!headerCheck(headBytes)) {
    return { isValid: false, reason: CORRUPTED_FILE_ERROR(file.name) };
  }

  const structureOk = await validateStructure(extension, file, buffer);
  if (!structureOk) {
    return { isValid: false, reason: CORRUPTED_FILE_ERROR(file.name) };
  }

  return { isValid: true };
};
